import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSession } from "@/security/RouteGuards";
import { useFederatedUser } from "@/security/federation";
import { usePostHog } from "posthog-js/react";
import { consoleErrors, failedRequests } from "./failedRequestTracker";
import { aboveBottomBanners } from "@/components/banners/placements/bottomInsets";

const BUG_REPORT_SURVEY_ID = process.env.NEXT_PUBLIC_BUG_REPORT_SURVEY_ID;

export default function BugReportButton() {
  const { profile, activationStatus, plan } = useSession();
  const { federationToken } = useFederatedUser();
  const posthog = usePostHog();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [observed, setObserved] = useState("");
  const [expected, setExpected] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const snapshotRef = useRef<{
    consoleErrors: string[];
    failedRequests: string[];
  }>({
    consoleErrors: [],
    failedRequests: [],
  });

  const isFederated = !!federationToken;
  const isVerifiedWebshareStaff =
    profile?.email?.endsWith("@webshare.io") &&
    activationStatus?.email_is_verified;
  const visible = isFederated || isVerifiedWebshareStaff;

  useEffect(() => {
    if (open) {
      snapshotRef.current = {
        consoleErrors: [...consoleErrors],
        failedRequests: failedRequests.map(
          (r) => `${r.method} ${r.url} → ${r.status} (${r.duration}ms)`
        ),
      };
    }
  }, [open]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setTitle("");
    setObserved("");
    setExpected("");
    setSubmitted(false);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!title.trim() || !observed.trim() || !posthog) return;

    const planLabel = [plan?.proxy_type, plan?.proxy_subtype]
      .filter(Boolean)
      .join(" / ");

    const trimmedTitle = title.trim();
    const trimmedObserved = observed.trim();
    const trimmedExpected = expected.trim();

    posthog.capture("survey sent", {
      $survey_id: BUG_REPORT_SURVEY_ID,
      $survey_name: "In-App Bug Report",
      $survey_completed: true,
      $survey_iteration: 1,
      $survey_iteration_start_date: "2026-03-16T19:24:00Z",
      $survey_submission_id: crypto.randomUUID(),
      // Standard survey response fields (keyed by question ID)
      "$survey_response_8a8659e7-e18a-4791-acdd-62fb3978ae5d": trimmedTitle,
      "$survey_response_34d018d4-5950-4a67-94f0-f894454f2e89": trimmedObserved,
      "$survey_response_b75dd6e0-a6f9-4323-81f8-8e93eebfff20": trimmedExpected,
      $survey_questions: [
        {
          id: "8a8659e7-e18a-4791-acdd-62fb3978ae5d",
          question: "Bug title",
          response: trimmedTitle,
        },
        {
          id: "34d018d4-5950-4a67-94f0-f894454f2e89",
          question: "What happened? (observed behavior)",
          response: trimmedObserved,
        },
        {
          id: "b75dd6e0-a6f9-4323-81f8-8e93eebfff20",
          question: "What did you expect to happen?",
          response: trimmedExpected,
        },
      ],
      // Hidden context fields
      console_errors: snapshotRef.current.consoleErrors.join("\n"),
      failed_requests: snapshotRef.current.failedRequests.join("\n"),
      is_federated: isFederated ? "true" : "false",
      customer_email: isFederated ? (profile?.email ?? "") : "",
      customer_plan: isFederated ? planLabel : "",
    });

    setSubmitted(true);
  }, [title, observed, expected, profile, plan, isFederated, posthog]);

  if (!visible) return null;

  return (
    <>
      <Fab
        size="small"
        color="error"
        sx={{
          position: "fixed",
          bottom: aboveBottomBanners(8),
          right: isFederated ? 8 : 160,
        }}
        onClick={handleOpen}
        aria-label="Report bug"
      >
        <BugReportIcon />
      </Fab>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        keepMounted={false}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Box flexGrow={1}>Report a Bug</Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {submitted ? (
            <Stack spacing={2} alignItems="center" py={2}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h6">Bug reported</Typography>
              <Typography variant="body2" color="text.secondary">
                Your report has been submitted. Thank you!
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2} pt={1}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                autoFocus
                placeholder="Short description of the bug"
              />
              <TextField
                label="What's wrong?"
                value={observed}
                onChange={(e) => setObserved(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                size="small"
                placeholder="Describe what you observed"
                required
              />
              <TextField
                label="What should happen instead?"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                size="small"
                placeholder="Describe the expected behavior"
              />

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  gutterBottom
                >
                  Auto-captured context
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  mt={0.5}
                >
                  <Chip
                    label={window.location.pathname}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={profile?.email ?? "unknown"}
                    size="small"
                    variant="outlined"
                  />
                  {isFederated && (
                    <Chip label="Federated" size="small" color="warning" />
                  )}
                  {snapshotRef.current.consoleErrors.length > 0 && (
                    <Chip
                      label={`${snapshotRef.current.consoleErrors.length} console errors`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {snapshotRef.current.failedRequests.length > 0 && (
                    <Chip
                      label={`${snapshotRef.current.failedRequests.length} failed requests`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>

        {!submitted && (
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!title.trim() || !observed.trim()}
            >
              File Bug
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
